import React, { useState, useRef, useCallback } from 'react';
import {
  ShareNetwork,
  Download,
  X,
  Star,
  MapPin,
  Check,
  SpinnerGap
} from '@phosphor-icons/react';
import {
  Dialog,
  DialogContent,
} from './ui/dialog';
import { Button } from './ui/button';

const CARD_WIDTH = 600;
const CARD_HEIGHT = 400;

const ShareCardModal = ({ open, onOpenChange, restaurant }) => {
  const canvasRef = useRef(null);
  const [generating, setGenerating] = useState(false);
  const [cardUrl, setCardUrl] = useState(null);

  // Fetch image with auth (cookie/token) and return a blob URL safe for canvas
  const loadImageAuth = useCallback(async (url) => {
    try {
      // Get token from localStorage for Authorization header
      const token = localStorage.getItem('token');
      const headers = {};
      if (token) headers['Authorization'] = `Bearer ${token}`;

      const resp = await fetch(url, {
        credentials: 'include',
        headers,
      });
      if (!resp.ok) throw new Error(`HTTP ${resp.status}`);
      const blob = await resp.blob();
      if (!blob.type.startsWith('image/')) throw new Error('Not an image');
      return URL.createObjectURL(blob);
    } catch {
      return null;
    }
  }, []);

  const generateCard = useCallback(async () => {
    if (!restaurant) return;
    setGenerating(true);

    const canvas = canvasRef.current;
    if (!canvas) return;
    const ctx = canvas.getContext('2d');
    canvas.width = CARD_WIDTH * 2; // 2x for retina
    canvas.height = CARD_HEIGHT * 2;
    ctx.scale(2, 2);

    // Background
    ctx.fillStyle = '#F9F8F5';
    ctx.fillRect(0, 0, CARD_WIDTH, CARD_HEIGHT);

    // Try loading hero image
    const heroUrl = restaurant.hero_image_url || '';
    const BACKEND = process.env.REACT_APP_BACKEND_URL || '';
    const fullHeroUrl = heroUrl.startsWith('/') ? `${BACKEND}${heroUrl.startsWith('/api') ? '' : '/api'}${heroUrl}` : heroUrl;

    try {
      if (fullHeroUrl) {
        // Fetch with auth, then load blob URL into canvas (no CORS issues)
        const blobUrl = await loadImageAuth(fullHeroUrl);
        if (!blobUrl) throw new Error('auth fetch failed');

        const img = new Image();
        await new Promise((resolve, reject) => {
          const timeout = setTimeout(() => reject(new Error('timeout')), 5000);
          img.onload = () => { clearTimeout(timeout); resolve(); };
          img.onerror = () => { clearTimeout(timeout); reject(new Error('load failed')); };
          img.src = blobUrl;
        });
        // Clean up blob URL after loading
        URL.revokeObjectURL(blobUrl);

        // Draw image with overlay
        ctx.save();
        ctx.beginPath();
        ctx.rect(0, 0, CARD_WIDTH, 200);
        ctx.clip();
        const scale = Math.max(CARD_WIDTH / img.width, 200 / img.height);
        const x = (CARD_WIDTH - img.width * scale) / 2;
        const y = (200 - img.height * scale) / 2;
        ctx.drawImage(img, x, y, img.width * scale, img.height * scale);
        // Gradient overlay
        const grad = ctx.createLinearGradient(0, 100, 0, 200);
        grad.addColorStop(0, 'rgba(26,25,24,0)');
        grad.addColorStop(1, 'rgba(26,25,24,0.7)');
        ctx.fillStyle = grad;
        ctx.fillRect(0, 0, CARD_WIDTH, 200);
        ctx.restore();
      } else {
        throw new Error('no image');
      }
    } catch {
      // Fallback solid color with pattern
      const grad = ctx.createLinearGradient(0, 0, CARD_WIDTH, 200);
      grad.addColorStop(0, '#4B5945');
      grad.addColorStop(1, '#2C2826');
      ctx.fillStyle = grad;
      ctx.fillRect(0, 0, CARD_WIDTH, 200);
    }

    // Rating badge on image
    if (restaurant.aggregate_rating) {
      const badgeX = CARD_WIDTH - 70;
      const badgeY = 20;
      ctx.fillStyle = 'rgba(255,255,255,0.9)';
      roundRect(ctx, badgeX, badgeY, 50, 28, 14);
      ctx.fill();
      ctx.fillStyle = '#A94438';
      ctx.font = 'bold 14px sans-serif';
      ctx.fillText(`★ ${restaurant.aggregate_rating}`, badgeX + 8, badgeY + 19);
    }

    // Restaurant name
    ctx.fillStyle = '#FFFFFF';
    ctx.font = 'bold 28px Georgia, serif';
    const name = restaurant.name.length > 30 ? restaurant.name.slice(0, 28) + '...' : restaurant.name;
    ctx.fillText(name, 24, 180);

    // Content section
    const contentY = 220;

    // Suburb
    if (restaurant.suburb) {
      ctx.fillStyle = '#7A7873';
      ctx.font = '14px sans-serif';
      ctx.fillText(`📍 ${restaurant.suburb}, Cape Town`, 24, contentY);
    }

    // Cuisine tags
    if (restaurant.cuisine?.length > 0) {
      ctx.fillStyle = '#5A5752';
      ctx.font = '13px sans-serif';
      ctx.fillText(restaurant.cuisine.slice(0, 4).join(' · '), 24, contentY + 28);
    }

    // Stats row
    const statsY = contentY + 60;
    ctx.fillStyle = '#E8DCD0';
    roundRect(ctx, 24, statsY, CARD_WIDTH - 48, 50, 12);
    ctx.fill();

    // Visit count
    ctx.fillStyle = '#1A1918';
    ctx.font = 'bold 18px sans-serif';
    ctx.fillText(String(restaurant.visit_count || 0), 50, statsY + 32);
    ctx.fillStyle = '#7A7873';
    ctx.font = '11px sans-serif';
    ctx.fillText('visits', 50, statsY + 46);

    // Price range
    if (restaurant.price_range) {
      const priceText = 'R'.repeat(
        restaurant.price_range === 'budget' ? 1 :
        restaurant.price_range === 'mid' ? 2 :
        restaurant.price_range === 'premium' ? 3 : 4
      );
      ctx.fillStyle = '#4B5945';
      ctx.font = 'bold 18px sans-serif';
      ctx.fillText(priceText, 150, statsY + 32);
      ctx.fillStyle = '#7A7873';
      ctx.font = '11px sans-serif';
      ctx.fillText('price', 150, statsY + 46);
    }

    // State
    const stateText = restaurant.state === 'want_to_try' ? 'Want to Try' :
                      restaurant.state === 'planned' ? 'Planned' : 'Visited';
    ctx.fillStyle = '#A94438';
    ctx.font = 'bold 14px sans-serif';
    ctx.fillText(stateText, 260, statsY + 32);

    // Branding
    ctx.fillStyle = '#D4CFC8';
    ctx.fillRect(0, CARD_HEIGHT - 40, CARD_WIDTH, 40);
    ctx.fillStyle = '#A94438';
    ctx.font = 'bold 13px sans-serif';
    ctx.fillText('🍴 Dining Tracker', 24, CARD_HEIGHT - 15);
    ctx.fillStyle = '#9A9590';
    ctx.font = '11px sans-serif';
    ctx.fillText('Cape Town Edition', CARD_WIDTH - 130, CARD_HEIGHT - 15);

    const url = canvas.toDataURL('image/png');
    setCardUrl(url);
    setGenerating(false);
  }, [restaurant]);

  React.useEffect(() => {
    if (open && restaurant) {
      generateCard();
    } else {
      setCardUrl(null);
    }
  }, [open, restaurant, generateCard]);

  const handleDownload = () => {
    if (!cardUrl) return;
    const a = document.createElement('a');
    a.href = cardUrl;
    a.download = `${restaurant.name.replace(/[^a-zA-Z0-9]/g, '_')}_share.png`;
    a.click();
  };

  const handleShare = async () => {
    if (!cardUrl || !navigator.share) {
      handleDownload();
      return;
    }
    try {
      const response = await fetch(cardUrl);
      const blob = await response.blob();
      const file = new File([blob], `${restaurant.name}_share.png`, { type: 'image/png' });
      await navigator.share({
        title: `${restaurant.name} - Dining Tracker`,
        files: [file],
      });
    } catch {
      handleDownload();
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-[650px]" data-testid="share-card-modal">
        <div className="space-y-4">
          <h2 className="font-heading text-2xl font-semibold text-[#1A1918]">Share Card</h2>

          <canvas ref={canvasRef} className="hidden" />

          {generating ? (
            <div className="flex items-center justify-center py-12">
              <SpinnerGap className="w-10 h-10 animate-spin text-[#A94438]" />
            </div>
          ) : cardUrl ? (
            <div className="rounded-xl overflow-hidden border border-[#EAE8E3] shadow-md">
              <img
                src={cardUrl}
                alt={`${restaurant?.name} share card`}
                className="w-full"
                data-testid="share-card-image"
              />
            </div>
          ) : null}

          <div className="flex gap-3">
            <Button
              onClick={handleDownload}
              variant="outline"
              className="flex-1"
              disabled={!cardUrl}
              data-testid="download-card-btn"
            >
              <Download weight="bold" className="w-5 h-5 mr-2" />
              Download
            </Button>
            <Button
              onClick={handleShare}
              className="flex-1 btn-primary"
              disabled={!cardUrl}
              data-testid="share-card-btn"
            >
              <ShareNetwork weight="bold" className="w-5 h-5 mr-2" />
              Share
            </Button>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
};

function roundRect(ctx, x, y, w, h, r) {
  ctx.beginPath();
  ctx.moveTo(x + r, y);
  ctx.arcTo(x + w, y, x + w, y + h, r);
  ctx.arcTo(x + w, y + h, x, y + h, r);
  ctx.arcTo(x, y + h, x, y, r);
  ctx.arcTo(x, y, x + w, y, r);
  ctx.closePath();
}

export default ShareCardModal;
